import React, { useEffect, useState } from "react";
import { Download, RotateCw, Trash2, AlertCircle } from "lucide-react";
import api from "../../lib/api";
import { API } from "../../lib/api";
import { toast } from "sonner";

const fmtBytes = (n) => {
    if (!n) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let v = n; let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const BackupsPanel = () => {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ retention_days: 30, backup_dir: "" });
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/backups");
            setRows(data.backups || []);
            setMeta({ retention_days: data.retention_days, backup_dir: data.backup_dir });
        } catch (e) {
            toast.error("Failed to load backups");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/admin/backups/run");
            toast.success(`Backup complete · ${data.document_count} docs · ${fmtBytes(data.file_size_bytes)}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backup failed");
        } finally {
            setRunning(false);
        }
    };

    const downloadOne = async (id, name) => {
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${API}/admin/backups/${id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = name; a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Could not download backup");
        }
    };

    const removeOne = async (id) => {
        if (!window.confirm("Delete this backup permanently?")) return;
        try {
            await api.delete(`/admin/backups/${id}`);
            toast.success("Backup deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="backups-panel" className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Backups</h2>
                    <p className="text-sm text-[#4A5568] mt-1">
                        Nightly snapshots of all collections + site settings as gzipped tar archives. Auto-runs once daily (02:00-05:00 UTC). Older than {meta.retention_days} days are pruned.
                    </p>
                </div>
                <button
                    onClick={runNow}
                    disabled={running}
                    data-testid="backups-run-now-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] text-white px-4 py-2 rounded-full text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
                >
                    <RotateCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} strokeWidth={1.5} />
                    {running ? "Backing up…" : "Run backup now"}
                </button>
            </div>

            <div className="bg-white border border-black/10 rounded-2xl overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-[#4A5568]" data-testid="backups-loading">Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center" data-testid="backups-empty">
                        <AlertCircle className="w-8 h-8 text-[#4A5568] mx-auto mb-3" strokeWidth={1.5} />
                        <p className="text-sm text-[#4A5568]">No backups yet. Run one now or wait for the nightly cron.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F4F1EA] text-[#4A5568] text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium">Created</th>
                                    <th className="text-left px-4 py-3 font-medium">File</th>
                                    <th className="text-right px-4 py-3 font-medium">Size</th>
                                    <th className="text-right px-4 py-3 font-medium">Docs</th>
                                    <th className="text-right px-4 py-3 font-medium">Cols</th>
                                    <th className="text-left px-4 py-3 font-medium">Source</th>
                                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} data-testid={`backup-row-${r.id}`} className="border-t border-black/5 hover:bg-[#F4F1EA]/40 transition">
                                        <td className="px-4 py-3 text-[#1C252E]">{fmtDate(r.created_at)}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-[#4A5568]">{r.file_name}</td>
                                        <td className="px-4 py-3 text-right text-[#1C252E]">{fmtBytes(r.file_size_bytes)}</td>
                                        <td className="px-4 py-3 text-right text-[#1C252E]">{(r.document_count || 0).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right text-[#1C252E]">{r.collections_count}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${r.auto_fired ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
                                                {r.auto_fired ? "auto" : "manual"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <button
                                                    onClick={() => downloadOne(r.id, r.file_name)}
                                                    data-testid={`backup-download-${r.id}`}
                                                    aria-label="Download backup"
                                                    className="p-1.5 text-[#4A5568] hover:text-[#1C252E] hover:bg-[#F4F1EA] rounded-md transition"
                                                >
                                                    <Download className="w-4 h-4" strokeWidth={1.5} />
                                                </button>
                                                <button
                                                    onClick={() => removeOne(r.id)}
                                                    data-testid={`backup-delete-${r.id}`}
                                                    aria-label="Delete backup"
                                                    className="p-1.5 text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10 rounded-md transition"
                                                >
                                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {meta.backup_dir && (
                <p className="text-xs text-[#4A5568]">
                    Archives stored on the server at <code className="font-mono">{meta.backup_dir}</code>. Use the "Save to GitHub" feature or your hosting provider's snapshot tool for off-site copies.
                </p>
            )}
        </div>
    );
};

export default BackupsPanel;
